import { Appearance, Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";

import config from "@/config";

let stripeClient: Stripe | undefined;
let stripePromise: Promise<Stripe> | undefined;

export const ensureStripe = async (): Promise<Stripe> => {
  if (stripeClient) {
    return stripeClient;
  }
  if (!stripePromise) {
    if (!config.stripePublishableKey) {
      throw new Error("Missing stripe publishable key");
    }
    stripePromise = loadStripe(config.stripePublishableKey).then((stripe) => {
      if (!stripe) {
        throw new Error("Failed to load Stripe.js (check publishable key and network)");
      }
      stripeClient = stripe;
      return stripe;
    });
  }
  return stripePromise;
};

export const getStripeClient = (): Stripe => {
  if (!stripeClient) {
    throw new Error("Must init stripe client");
  }
  return stripeClient;
};

// The app is forced into dark mode globally (see main.ts), so Stripe Elements
// iframes need their own appearance config — they don't inherit Tailwind's
// `dark:` classes. Values mirror tokens in main.css.
export const stripeDarkAppearance: Appearance = {
  theme: "night",
  variables: {
    colorPrimary: "#4459ff",
    colorBackground: "#1b1b1b",
    colorText: "#ffffff",
    colorTextSecondary: "#a1a8b7",
    colorTextPlaceholder: "#606880",
    colorDanger: "#ff5c16",
    colorIconTab: "#a1a8b7",
    colorIconTabSelected: "#ffffff",
    fontFamily: "'Geist', ui-sans-serif, system-ui, sans-serif",
    borderRadius: "8px",
    spacingUnit: "4px",
  },
  rules: {
    ".Input": {
      backgroundColor: "#1b1b1b",
      border: "1px solid #2e3038",
      color: "#ffffff",
    },
    ".Input:focus": {
      border: "1px solid #4459ff",
      boxShadow: "0 0 0 1px #4459ff",
    },
    ".Input--invalid": {
      border: "1px solid #ff5c16",
      boxShadow: "none",
    },
    ".Label": {
      color: "#ffffff",
    },
    ".Tab": {
      backgroundColor: "#1b1b1b",
      border: "1px solid #2e3038",
      color: "#ffffff",
    },
    ".Tab:hover": {
      backgroundColor: "#242628",
    },
    ".Tab--selected": {
      backgroundColor: "#242628",
      border: "1px solid #4459ff",
    },
    ".TabIcon--selected": {
      fill: "#ffffff",
    },
    ".Block": {
      backgroundColor: "#1b1b1b",
      border: "1px solid #2e3038",
    },
    ".Error": {
      color: "#ff5c16",
    },
  },
};
